import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, CalendarDays, Check, ChevronLeft, ChevronRight,
  Clock, Minus, Plus, ShieldCheck, Users,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";
import { useLang, WHATSAPP_URL } from "@/i18n";

function pricePreview(service, participants, hours, resident) {
  const p = service.pricing;
  let subtotal = 0;
  let tessera = 0;
  if (p.type === "group_tier") {
    const tier = p.tiers.find((x) => participants <= x.max);
    subtotal = tier.pp * participants;
    tessera = (p.tessera || 0) * participants;
  } else if (p.type === "flat") {
    subtotal = p.pp * participants;
    tessera = (p.tessera || 0) * participants;
  } else {
    subtotal = (p.rates[String(hours)] || 0) * participants;
  }
  const discount = resident ? Math.round(subtotal * 0.2) : 0;
  return { subtotal, discount, tessera, total: subtotal - discount + tessera };
}

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function SectionHead({ n, children }) {
  return (
    <div className="flex items-baseline gap-4 mb-6">
      <span className="font-mono text-xs tracking-[0.25em] text-teal">{n}</span>
      <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-lake-deep">{children}</h2>
    </div>
  );
}

export default function Booking() {
  const { lang, t } = useLang();
  const tb = t.booking;
  const [params] = useSearchParams();
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState(params.get("service") || "");
  const [monthOffset, setMonthOffset] = useState(0);
  const [availability, setAvailability] = useState({});
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState(null);
  const [participants, setParticipants] = useState(1);
  const [hours, setHours] = useState(1);
  const [resident, setResident] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const service = services.find((s) => s.id === serviceId);
  const isRental = service?.pricing.type === "rental";

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      if (!params.get("service") && r.data.length) setServiceId((cur) => cur || r.data[0].id);
    }).catch(() => {});
  }, [params]);

  const viewDate = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + monthOffset);
    return d;
  }, [monthOffset]);
  const monthStr = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, "0")}`;

  useEffect(() => {
    if (!serviceId) return;
    setDate("");
    setSlot(null);
    api.get(`/availability?service_id=${serviceId}&month=${monthStr}`)
      .then((r) => setAvailability(r.data))
      .catch(() => setAvailability({}));
  }, [serviceId, monthStr]);

  useEffect(() => {
    if (service) setParticipants(service.min_participants);
  }, [serviceId, services]); // eslint-disable-line

  const calendar = useMemo(() => {
    const y = viewDate.getFullYear();
    const m = viewDate.getMonth();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const firstDay = (new Date(y, m, 1).getDay() + 6) % 7;
    const today = toISO(new Date());
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const slots = availability[iso] || [];
      const remaining = slots.reduce((a, s) => a + s.remaining, 0);
      cells.push({ iso, day: d, remaining, disabled: iso < today || remaining === 0 });
    }
    return cells;
  }, [viewDate, availability]);

  const price = service ? pricePreview(service, participants, hours, resident) : null;
  const canSubmit =
    service && slot && form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim().length >= 6;

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        service_id: serviceId,
        slot_id: slot.slot_id,
        name: form.name,
        email: form.email,
        phone: form.phone,
        participants,
        hours,
        is_resident: resident,
        notes: form.notes,
        lang,
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
      const r = await api.get(`/availability?service_id=${serviceId}&month=${monthStr}`).catch(() => null);
      if (r) {
        setAvailability(r.data);
        setSlot(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (iso) => {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  };

  if (result) {
    const s = services.find((x) => x.id === result.service_id);
    const waText = encodeURIComponent(
      `Ciao! Ho prenotato: ${result.code} — ${s ? s.title_it : result.service_id} del ${result.date} ore ${result.time} (${result.participants} pax). ${result.name}`
    );
    return (
      <div className="min-h-screen bg-travertine grain relative flex items-center justify-center px-5 py-20">
        <motion.div
          data-testid="booking-success-card"
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-xl bg-sand border border-lake-deep/10 rounded-lg overflow-hidden shadow-[0_40px_80px_-30px_rgba(8,27,38,0.4)]"
        >
          <div className="bg-lake-deep px-8 py-10 text-center grain relative">
            <span className="inline-flex w-14 h-14 rounded-full bg-teal/20 border border-teal-light/40 items-center justify-center">
              <Check className="w-7 h-7 text-gold" />
            </span>
            <h1 className="mt-5 font-serif text-3xl sm:text-4xl text-glacier tracking-tight">{tb.successTitle}</h1>
            <p className="mt-3 text-glacier/65 text-sm max-w-sm mx-auto">{tb.successSub}</p>
            <div className="mt-6 inline-block border border-gold/50 bg-gold/10 rounded-md px-6 py-3">
              <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80">{tb.code}</span>
              <span data-testid="booking-code" className="font-mono text-2xl sm:text-3xl font-semibold text-gold tracking-wider">
                {result.code}
              </span>
            </div>
          </div>
          <div className="px-8 py-8 space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-lake-deep/50">{tb.what}</p>
                <p className="mt-1 font-semibold text-lake-deep">{s ? (lang === "it" ? s.title_it : s.title_en) : ""}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-lake-deep/50">{tb.when}</p>
                <p className="mt-1 font-semibold text-lake-deep capitalize">{fmtDate(result.date)} · {result.time}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-lake-deep/50">{tb.who}</p>
                <p className="mt-1 font-semibold text-lake-deep">{result.participants}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-lake-deep/50">{tb.total}</p>
                <p data-testid="booking-total" className="mt-1 font-mono font-semibold text-lake-deep">€{result.price.total}</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5 bg-gold/15 border border-gold/40 rounded-md px-4 py-3 text-sm text-lake-deep">
              <ShieldCheck className="w-5 h-5 text-teal shrink-0 mt-0.5" />
              {tb.noPayment}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <a
                data-testid="success-whatsapp-btn"
                href={`https://wa.me/393381178389?text=${waText}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center bg-[#25D366] text-white font-semibold px-6 py-3.5 rounded-full text-sm hover:opacity-90 transition-opacity"
              >
                {tb.whatsapp}
              </a>
              <Link
                data-testid="success-home-btn"
                to="/"
                className="flex-1 text-center border border-lake-deep/20 text-lake-deep font-semibold px-6 py-3.5 rounded-full text-sm hover:bg-lake-deep hover:text-glacier transition-colors"
              >
                {tb.backHome}
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="min-h-screen bg-travertine grain relative">
      <div className="bg-lake-deep pt-28 sm:pt-32 pb-14 grain relative">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Link to="/" data-testid="booking-back-link" className="inline-flex items-center gap-2 text-glacier/60 hover:text-glacier text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> {tb.back}
          </Link>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-gold">{tb.eyebrow}</p>
            <h1 className="mt-3 font-serif text-4xl sm:text-6xl text-glacier tracking-tight">{tb.title}</h1>
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12">
        <div className="space-y-14">
          <section>
            <SectionHead n="01">{tb.step1}</SectionHead>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {services.map((s) => (
                <button
                  key={s.id}
                  data-testid={`booking-service-${s.id}`}
                  onClick={() => setServiceId(s.id)}
                  className={`text-left border rounded-md px-5 py-4 transition-colors duration-300 ${
                    serviceId === s.id
                      ? "bg-lake-deep text-glacier border-lake-deep"
                      : "bg-sand border-lake-deep/15 hover:border-lake-deep/40"
                  }`}
                >
                  <span className={`block font-mono text-[10px] uppercase tracking-[0.2em] ${serviceId === s.id ? "text-gold" : "text-teal"}`}>
                    {(lang === "it"
                      ? { corsi: "Corso kayak", escursioni: "Escursione", natanti: "Noleggio", vela: "Corso vela" }
                      : { corsi: "Kayak course", escursioni: "Tour", natanti: "Rental", vela: "Sailing course" })[s.category] || s.category}
                  </span>
                  <span className="block mt-1 font-serif text-lg leading-tight">
                    {lang === "it" ? s.title_it : s.title_en}
                  </span>
                  <span className={`block mt-1 font-mono text-xs ${serviceId === s.id ? "text-glacier/60" : "text-lake-deep/50"}`}>
                    {lang === "it" ? s.duration_it : s.duration_en}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section>
            <SectionHead n="02">{tb.step2}</SectionHead>
            <div data-testid="booking-date-picker" className="bg-sand border border-lake-deep/10 rounded-md p-5 sm:p-6">
              <div className="flex items-center justify-between mb-5">
                <button
                  data-testid="calendar-prev-month"
                  onClick={() => setMonthOffset((o) => Math.max(0, o - 1))}
                  disabled={monthOffset === 0}
                  className="w-9 h-9 rounded-full border border-lake-deep/15 flex items-center justify-center disabled:opacity-30 hover:bg-lake-deep hover:text-glacier transition-colors"
                  aria-label="previous month"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="font-serif text-xl text-lake-deep capitalize">
                  {t.months[viewDate.getMonth()]} {viewDate.getFullYear()}
                </span>
                <button
                  data-testid="calendar-next-month"
                  onClick={() => setMonthOffset((o) => Math.min(2, o + 1))}
                  disabled={monthOffset === 2}
                  className="w-9 h-9 rounded-full border border-lake-deep/15 flex items-center justify-center disabled:opacity-30 hover:bg-lake-deep hover:text-glacier transition-colors"
                  aria-label="next month"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center font-mono text-[10px] uppercase tracking-wider text-lake-deep/45 mb-2">
                {t.days.map((d) => <span key={d}>{d}</span>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendar.map((c, i) =>
                  c === null ? (
                    <span key={`e${i}`} />
                  ) : (
                    <button
                      key={c.iso}
                      data-testid={`calendar-day-${c.iso}`}
                      disabled={c.disabled}
                      onClick={() => { setDate(c.iso); setSlot(null); }}
                      className={`aspect-square rounded-md flex flex-col items-center justify-center text-sm transition-colors duration-200 ${
                        date === c.iso
                          ? "bg-teal text-white font-semibold"
                          : c.disabled
                            ? "text-lake-deep/25 cursor-not-allowed"
                            : "hover:bg-glacier text-lake-deep"
                      }`}
                    >
                      <span>{c.day}</span>
                      {!c.disabled && (
                        <span className={`text-[9px] font-mono ${date === c.iso ? "text-white/85" : "text-teal"}`}>
                          {c.remaining}
                        </span>
                      )}
                    </button>
                  )
                )}
              </div>
              {Object.keys(availability).length === 0 && (
                <p className="mt-4 text-sm text-terracotta font-mono">{tb.noSlots}</p>
              )}
            </div>
          </section>

          <AnimatePresence>
            {date && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <SectionHead n="03">{tb.step3}</SectionHead>
                <div className="flex flex-wrap gap-3">
                  {(availability[date] || []).map((s) => (
                    <button
                      key={s.slot_id}
                      data-testid={`booking-time-slot-${s.time}`}
                      onClick={() => setSlot(s)}
                      className={`border rounded-full px-6 py-3 font-mono text-sm transition-colors duration-300 ${
                        slot?.slot_id === s.slot_id
                          ? "bg-lake-deep text-glacier border-lake-deep"
                          : "bg-sand border-lake-deep/15 hover:border-lake-deep/40"
                      }`}
                    >
                      <Clock className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />
                      {s.time}
                      <span className={`ml-2 text-[10px] ${slot?.slot_id === s.slot_id ? "text-gold" : "text-teal"}`}>
                        {s.remaining} {tb.spotsLeft}
                      </span>
                    </button>
                  ))}
                </div>
              </motion.section>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {slot && service && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-10"
              >
                <div>
                  <SectionHead n="04">{tb.step4}</SectionHead>
                  <div className="bg-sand border border-lake-deep/10 rounded-md p-6 space-y-6">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm font-medium text-lake-deep">
                        <Users className="w-4 h-4 text-teal" />
                        {isRental ? tb.kayaks : tb.participants}
                        {service.min_participants > 1 && (
                          <span className="font-mono text-[11px] text-terracotta ml-2">
                            {tb.minNotice.replace("{n}", service.min_participants)}
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-3">
                        <button
                          data-testid="participants-minus"
                          onClick={() => setParticipants((p) => Math.max(service.min_participants, p - 1))}
                          className="w-9 h-9 rounded-full border border-lake-deep/20 flex items-center justify-center hover:bg-lake-deep hover:text-glacier transition-colors"
                          aria-label="minus"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span data-testid="participants-count" className="font-mono text-xl font-semibold w-8 text-center">{participants}</span>
                        <button
                          data-testid="participants-plus"
                          onClick={() => setParticipants((p) => Math.min(slot.remaining, p + 1))}
                          className="w-9 h-9 rounded-full border border-lake-deep/20 flex items-center justify-center hover:bg-lake-deep hover:text-glacier transition-colors"
                          aria-label="plus"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {isRental && (
                      <div>
                        <span className="block text-sm font-medium text-lake-deep mb-3">{tb.hours}</span>
                        <div className="flex gap-3">
                          {[1, 2, 3].map((h) => (
                            <button
                              key={h}
                              data-testid={`rental-hours-${h}`}
                              onClick={() => setHours(h)}
                              className={`flex-1 border rounded-md py-3 font-mono text-sm transition-colors duration-300 ${
                                hours === h ? "bg-teal text-white border-teal" : "border-lake-deep/15 hover:border-lake-deep/40"
                              }`}
                            >
                              {h} {h === 1 ? tb.hour : tb.hours2} · €{service.pricing.rates[String(h)]}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        data-testid="resident-checkbox"
                        type="checkbox"
                        checked={resident}
                        onChange={(e) => setResident(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-[#0E768A]"
                      />
                      <span>
                        <span className="block text-sm font-medium text-lake-deep">{tb.resident}</span>
                        <span className="block text-xs text-lake-deep/55 mt-0.5">{tb.residentNote}</span>
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <SectionHead n="05">{tb.step5}</SectionHead>
                  <div className="bg-sand border border-lake-deep/10 rounded-md p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-lake-deep/55 mb-2">{tb.name}</label>
                      <input
                        data-testid="booking-name-input"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full bg-transparent border-b border-lake-deep/25 focus:border-teal outline-none py-2 text-lake-deep"
                        placeholder="Mario Rossi"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-lake-deep/55 mb-2">{tb.phone}</label>
                      <input
                        data-testid="booking-phone-input"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full bg-transparent border-b border-lake-deep/25 focus:border-teal outline-none py-2 text-lake-deep"
                        placeholder="+39 333 000 0000"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-lake-deep/55 mb-2">{tb.email}</label>
                      <input
                        data-testid="booking-email-input"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full bg-transparent border-b border-lake-deep/25 focus:border-teal outline-none py-2 text-lake-deep"
                        placeholder="mario@esempio.it"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-lake-deep/55 mb-2">{tb.notes}</label>
                      <textarea
                        data-testid="booking-notes-input"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={2}
                        className="w-full bg-transparent border-b border-lake-deep/25 focus:border-teal outline-none py-2 text-lake-deep resize-none"
                      />
                    </div>
                  </div>
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        </div>

        <aside className="lg:sticky lg:top-28 h-fit">
          <div data-testid="booking-summary" className="bg-lake-deep text-glacier rounded-md p-7 grain relative overflow-hidden">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-gold">{tb.summary}</h3>
            {service ? (
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-glacier/60">{tb.what}</span>
                  <span className="font-serif text-base text-right">{lang === "it" ? service.title_it : service.title_en}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-glacier/60">{tb.when}</span>
                  <span className="text-right">{date ? `${fmtDate(date)} · ${slot ? slot.time : ""}` : "—"}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-glacier/60">{isRental ? tb.kayaks : tb.who}</span>
                  <span>{participants}{isRental ? ` · ${hours}h` : ""}</span>
                </div>
                {price && (
                  <div className="border-t hairline-light pt-4 mt-4 space-y-2 font-mono text-sm">
                    <div className="flex justify-between"><span className="text-glacier/60">{tb.subtotal}</span><span>€{price.subtotal}</span></div>
                    {price.discount > 0 && (
                      <div className="flex justify-between text-gold"><span>{tb.discount}</span><span>−€{price.discount}</span></div>
                    )}
                    {price.tessera > 0 && (
                      <div className="flex justify-between"><span className="text-glacier/60">{tb.tessera}</span><span>€{price.tessera}</span></div>
                    )}
                    <div className="flex justify-between text-lg font-semibold pt-2 border-t hairline-light">
                      <span>{tb.total}</span>
                      <span data-testid="summary-total" className="text-gold">€{price.total}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-5 text-glacier/50 text-sm">{tb.step1}…</p>
            )}
            <button
              data-testid="booking-submit-btn"
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="mt-7 w-full bg-gold text-lake-deep font-semibold py-4 rounded-full text-sm hover:bg-glacier transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? tb.submitting : tb.submit}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>
            <p className="mt-4 flex items-start gap-2 text-[11px] leading-relaxed text-glacier/55">
              <ShieldCheck className="w-4 h-4 text-teal-light shrink-0" />
              {tb.noPayment}
            </p>
            <p className="mt-3 flex items-center gap-2 font-mono text-[10px] text-glacier/35">
              <CalendarDays className="w-3.5 h-3.5" /> CVM · Manerba del Garda
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
